import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { 
  Brain, 
  Zap, 
  Shield, 
  Users, 
  BarChart, 
  Sparkles,
  ArrowRight,
  Check
} from 'lucide-react'

export function Landing() {
  const features = [
    {
      icon: Brain,
      title: 'AI-Powered Knowledge Base',
      description: 'Transform your documents into intelligent, searchable knowledge bases with RAG technology.'
    },
    {
      icon: Zap,
      title: 'Instant Answers',
      description: 'Provide instant, accurate answers to your customers through AI-powered chat assistants.'
    },
    {
      icon: Shield,
      title: 'Secure & Compliant',
      description: 'Enterprise-grade security with role-based access control and data encryption.'
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Invite team members, manage roles, and collaborate on knowledge bases.'
    },
    {
      icon: BarChart,
      title: 'Analytics & Insights',
      description: 'Track usage, monitor performance, and gain insights from chat interactions.'
    },
    {
      icon: Sparkles,
      title: 'Customizable Widgets',
      description: 'Embed beautiful, customizable chat widgets on your website in minutes.'
    }
  ]

  const benefits = [
    'Create unlimited knowledge bases',
    'AI-powered search and retrieval',
    'Real-time chat interactions',
    'Team collaboration tools',
    'Advanced analytics dashboard',
    'Custom branding options'
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {/* Navigation */}
      <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold">Chatterpillar</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/signup">
                <Button>Get Started</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 items-center">
          <div className="space-y-6">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              Transform Your Knowledge Into
              <span className="text-primary"> AI-Powered Conversations</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Build intelligent knowledge bases and deploy AI chat assistants that provide instant,
              accurate answers to your customers - all in one powerful platform.
            </p>
            <div className="flex flex-col gap-4 sm:flex-row">
              <Link to="/signup">
                <Button size="lg" className="w-full sm:w-auto">
                  Create Free Workspace
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  Sign In
                </Button>
              </Link>
            </div>
            
            {/* Key Benefits */}
            <div className="pt-4">
              <p className="text-sm font-medium mb-3">What you get:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-lg blur-3xl"></div>
            <Card className="relative">
              <CardContent className="p-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Brain className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">Upload Documents</p>
                      <p className="text-sm text-muted-foreground">
                        PDFs, Word docs, text files, and more
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Zap className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">AI Processing</p>
                      <p className="text-sm text-muted-foreground">
                        Automatic indexing with RAGflow technology
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Sparkles className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">Deploy Chat Widget</p>
                      <p className="text-sm text-muted-foreground">
                        Embed on your website with one line of code
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Everything You Need</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Powerful features to help you build, deploy, and manage AI-powered knowledge bases
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="mb-4">
                  <div className="p-3 bg-primary/10 rounded-lg w-fit">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20">
        <Card className="bg-primary text-primary-foreground">
          <CardContent className="p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
              Create your free workspace and start building AI-powered knowledge bases today.
              No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup">
                <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                  Create Free Workspace
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Brain className="h-6 w-6 text-primary" />
              <span className="font-semibold">Chatterpillar</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2024 Chatterpillar. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
